import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Zap, BarChart3, Brain, Users, Calendar, FileText } from "lucide-react";

const DISMISSED_KEY = "ai.supergrow.mcp_launch_dismissed";
const EXPIRY_DATE = new Date("2026-05-21T00:00:00");

const INDIVIDUAL_HIGHLIGHTS = [
  {
    icon: FileText,
    title: "Write & Publish",
    description: "Draft posts in your Content DNA voice and queue them for the best time.",
  },
  {
    icon: Zap,
    title: "Repurpose Content",
    description: "Turn YouTube videos, articles, and PDFs into LinkedIn posts instantly.",
  },
  {
    icon: BarChart3,
    title: "Analytics & Insights",
    description: "Track impressions, follower growth, and see what's working.",
  },
  {
    icon: Brain,
    title: "Score & Improve",
    description: "Get your drafts scored on hook quality, clarity, and CTA — then auto-improve.",
  },
];

const ORG_HIGHLIGHTS = [
  {
    icon: Users,
    title: "Team Management",
    description: "See how all team members are performing and who needs a nudge.",
  },
  {
    icon: FileText,
    title: "Draft for Members",
    description: "Create posts for any team member using their Content DNA.",
  },
  {
    icon: BarChart3,
    title: "Growth Comparison",
    description: "Compare follower growth and engagement across all workspaces.",
  },
  {
    icon: Calendar,
    title: "Pipeline & Approvals",
    description: "Monitor draft pipelines, approval queues, and flag low-quality posts.",
  },
];

export default function McpLaunchModal() {
  const [isVisible, setIsVisible] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const isOrg = location.pathname.startsWith("/org");
  const highlights = isOrg ? ORG_HIGHLIGHTS : INDIVIDUAL_HIGHLIGHTS;

  useEffect(() => {
    const dismissed = localStorage.getItem(DISMISSED_KEY);
    if (!dismissed && new Date() < EXPIRY_DATE) {
      setIsVisible(true);
    }
  }, []);

  function handleDismiss() {
    localStorage.setItem(DISMISSED_KEY, "true");
    setIsVisible(false);
  }

  function handleSetup() {
    localStorage.setItem(DISMISSED_KEY, "true");
    setIsVisible(false);
    navigate(isOrg ? "/org/integrations/mcp" : "/mcp");
  }

  if (!isVisible) return null;

  return (
    <div className="fixed bottom-4 left-4 z-50 lg:left-20 w-full max-w-md">
      <div className="relative overflow-hidden shadow-2xl rounded-xl border border-blue-200">
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-50 to-indigo-50 px-4 py-5 sm:px-6 border-b border-blue-100">
          <div className="flex items-center justify-between gap-4">
            <div className="flex items-center gap-2">
              <p className="text-lg font-semibold text-gray-900">Supergrow MCP is here</p>
              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold bg-blue-500 text-white">
                New
              </span>
            </div>
            <button
              type="button"
              className="text-gray-400 rounded-lg hover:text-gray-500 focus:outline-none"
              onClick={handleDismiss}
              aria-label="Dismiss"
            >
              <svg
                className="w-6 h-6"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth="2"
              >
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          <p className="text-sm text-gray-600 mt-2">
            {isOrg
              ? "Connect Supergrow to AI tools like Claude and ChatGPT to manage your entire team's LinkedIn presence through natural conversation."
              : "Connect Supergrow to AI tools like Claude and ChatGPT to create, schedule, and analyze your LinkedIn content through natural conversation."}
          </p>
        </div>

        {/* Highlights */}
        <div className="bg-white px-4 sm:px-6 pt-4 pb-2">
          <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">
            What you can do
          </p>
          <div className="grid grid-cols-2 gap-2.5">
            {highlights.map((item) => (
              <div
                key={item.title}
                className="bg-gray-50 border border-gray-100 rounded-lg p-3"
              >
                <div className="flex items-center gap-2 mb-1">
                  <item.icon className="w-4 h-4 text-blue-500 shrink-0" />
                  <p className="text-sm font-medium text-gray-900">{item.title}</p>
                </div>
                <p className="text-xs text-gray-500 leading-relaxed">{item.description}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Footer */}
        <div className="bg-white px-4 py-4 sm:px-6">
          <div className="flex flex-col gap-3 sm:items-center sm:justify-start sm:flex-row-reverse">
            <button
              type="button"
              onClick={handleSetup}
              className="inline-flex items-center w-full sm:w-auto justify-center gap-2 transition-all duration-150 rounded-full bg-blue-500 px-3 py-2 sm:px-4 sm:py-2.5 text-sm font-semibold text-white hover:bg-blue-600 group shadow-xs"
            >
              Set up MCP
            </button>
            <button
              type="button"
              onClick={handleDismiss}
              className="inline-flex w-full sm:w-auto items-center justify-center gap-2 transition-all duration-150 rounded-full bg-white px-3 py-2 sm:px-4 sm:py-2.5 text-sm font-semibold text-gray-600 hover:text-gray-900 group shadow-xs ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
            >
              Maybe later
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
